// Players (spec/13 §8, F-ORG-05/-06): the customer as one organisation sees them. The list leads —
// search among the organisation's own players, or browse them — and a row opens the player in a large
// centred dialog over it (2026-10-07: a dialog, not a sheet from the side). Reading rides `players:read`; the
// notes, tags, wallet, ID check and ban actions inside the dialog ride their own permissions, each checked
// again by the server (spec/13 §1).
//
// Organisation-scoped, so the venue console and the organisation console share it: at a venue a ban is
// taken at that venue, and at the organisation it spans every venue.

import type { OrganizationPlayer } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Button, Chip, EmptyState, HeroStat, Table, type TableColumn, Tabs } from '@clavian/ui-web';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useScopeIds } from '../../shell/scope';
import { Forbidden, Page, SearchField, TableSkeleton, Toolbar } from '../../ui';
import { MovedPlayers, MovedSearch, useMovedSummary } from './MovedPlayers';
import { PlayerDialog } from './PlayerDialog';
import { playerName, usePlayerSearch } from './queries';

import '../people.css';

const PLAYERS_READ = 'players:read';

/** Wait until the operator pauses before searching, so a name is one request, not one a letter. */
function useDebounced(value: string, delayMs = 300): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

export function PlayersPage(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const canRead = access.status !== 'ready' || access.can(PLAYERS_READ);

  const [text, setText] = useState('');
  const q = useDebounced(text);
  const [selected, setSelected] = useState<string | null>(null);
  // Players moved from the café's previous system (F-HQ-40): a second list, only where a move brought players over.
  const moved = useMovedSummary();
  const [movedText, setMovedText] = useState('');
  const movedQ = useDebounced(movedText);
  const onlyPlatform = moved.platforms.length === 1 ? moved.platforms[0] : undefined;
  const platformName = onlyPlatform === undefined ? t('players.moved.platform.other') : t(`players.moved.platform.${onlyPlatform}`);

  const playersList = (
    <>
      <Toolbar label={t('players.toolbar')}>
        <SearchField label={t('players.search.label')} placeholder={t('players.search.placeholder')} value={text} onChange={(event) => setText(event.target.value)} autoFocus />
      </Toolbar>
      <PlayerList q={q} onOpen={setSelected} />
    </>
  );

  // The header's figures are the page already read (the same query the list below shares), never a count asked for.
  const loaded = usePlayerSearch(scope.organizationId, '', { enabled: canRead });
  const listed = loaded.data?.pages.flatMap((page) => page.data);
  const more = loaded.hasNextPage ? '+' : '';
  const hero =
    listed === undefined
      ? undefined
      : {
          stats: (
            <>
              <HeroStat label={t('players.hero.shown')} value={`${listed.length}${more}`} />
              <HeroStat label={t('players.hero.phone')} value={listed.filter((player) => player.phoneNumber !== null).length} of={`/ ${listed.length}${more}`} />
              <HeroStat label={t('players.hero.tagged')} value={listed.filter((player) => player.tags.length > 0).length} of={`/ ${listed.length}${more}`} />
            </>
          ),
        };

  return (
    <Page title={t('players.title')} description={t('players.intro')} hero={hero ?? {}}>
      {access.status === 'ready' && !canRead ? (
        <Forbidden message={t('players.notPermitted')} />
      ) : (
        <>
          {moved.shown && canRead ? (
            <Tabs
              className="console-players-tabs"
              label={t('players.tabs')}
              items={[
                { value: 'players', label: t('players.tab.players'), content: playersList },
                {
                  value: 'moved',
                  label: onlyPlatform === undefined ? t('players.tab.movedMany') : t('players.tab.moved', { platform: platformName }),
                  content: (
                    <>
                      <MovedSearch value={movedText} onChange={setMovedText} />
                      <MovedPlayers q={movedQ} platform={platformName} />
                    </>
                  ),
                },
              ]}
            />
          ) : (
            playersList
          )}
          <PlayerDialog playerId={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </Page>
  );
}

function PlayerList({ q, onOpen }: { readonly q: string; readonly onOpen: (playerId: string) => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const players = usePlayerSearch(scope.organizationId, q);

  const columns = useMemo<readonly TableColumn<OrganizationPlayer>[]>(
    () => [
      {
        key: 'player',
        header: t('players.column.player'),
        cell: (player) => (
          <div className="console-people-cell">
            <Avatar name={playerName(player)} size="sm" decorative />
            <div className="console-people-cell__who">
              <button type="button" className="console-people-cell__name" onClick={() => onOpen(player.id)}>
                {playerName(player)}
              </button>
              {player.username !== null && player.username !== '' && player.username !== playerName(player) ? <span className="console-subtle">@{player.username}</span> : null}
            </div>
          </div>
        ),
      },
      { key: 'phone', header: t('players.column.phone'), cell: (player) => <Contact value={player.phoneNumber} /> },
      { key: 'email', header: t('players.column.email'), cell: (player) => <Contact value={player.email} /> },
      {
        key: 'tags',
        header: t('players.column.tags'),
        cell: (player) =>
          player.tags.length === 0 ? (
            <span className="console-subtle">{t('players.noTags')}</span>
          ) : (
            <span className="console-people-tags">
              {player.tags.slice(0, 3).map((tag) => (
                <Chip key={tag}>{tag}</Chip>
              ))}
              {player.tags.length > 3 ? <span className="console-subtle">{t('players.moreTags', { count: player.tags.length - 3 })}</span> : null}
            </span>
          ),
      },
      {
        key: 'known',
        header: t('players.column.known'),
        cell: (player) => (
          <>
            {/* On a phone the row is a card with no column headers, so the date says what it is. */}
            <span className="console-players__known-label">{t('players.column.known')} </span>
            {format.date(player.knownSince, 'medium')}
          </>
        ),
      },
    ],
    [t, format, onOpen],
  );

  if (players.data === undefined) {
    return players.isError ? (
      <LoadFailure error={players.error} message={t('players.unavailable')} onRetry={() => void players.refetch()} />
    ) : (
      <TableSkeleton label={t('players.loading')} />
    );
  }

  const rows = players.data.pages.flatMap((page) => page.data);
  if (rows.length === 0) {
    return q.trim() === '' ? (
      <EmptyState icon={<Icon name="user" />} title={t('players.empty.title')} description={t('players.empty.body')} />
    ) : (
      <EmptyState size="compact" icon={<Icon name="user" />} title={t('players.noMatch', { query: q.trim() })} description={t('players.noMatchHint')} />
    );
  }

  return (
    <section aria-label={t('players.resultsLabel')}>
      <Table
        className="console-players-table"
        caption={t('players.caption')}
        captionHidden
        columns={columns}
        rows={rows}
        rowKey={(player) => player.id}
        empty={t('players.noMatch', { query: q.trim() })}
      />
      {players.hasNextPage ? (
        <div className="console-list-foot">
          <Button variant="ghost" size="sm" busy={players.isFetchingNextPage} onClick={() => void players.fetchNextPage()}>
            {t('players.more')}
          </Button>
        </div>
      ) : (
        <p className="console-subtle console-list-count" role="status">
          {t('players.count', { count: rows.length })}
        </p>
      )}
    </section>
  );
}

/** A masked contact detail, or "Not given" where there is none. `contactMasked` says it is masked. */
function Contact({ value }: { readonly value: string | null }): ReactNode {
  const { t } = useTranslation('console');
  if (value === null) return <span className="console-subtle">{t('players.notGiven')}</span>;
  return <span className="console-people-contact">{value}</span>;
}
